// The buttons of a rendered text's blocks. The renderer emits none (the
// sanitiser allows no button), so the view puts them in the DOM it has just
// written: Copy and, in a shell block of a reply, Run in a code block's head,
// and the zoom on a diagram. Copy puts the block's raw text on the clipboard;
// the button reads "Copied" for a moment (today's markdown/copy.ts).
import { COPIED_MS, copyText } from '../transcript/copy'

const COPY_LABEL = 'Copy'
const COPY_ARIA = 'Copy code'
const COPIED_LABEL = 'Copied'

const resets = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>()

/**
 * The code of the block a button sits in, as written: highlighting spans
 * contribute their text, and the newline that closes every fenced block is
 * left out, so a pasted command does not run on its own.
 */
export function codeBlockText(button: Element): string | null {
  const pre = button.closest('.md-code-block')?.querySelector(':scope > pre.md-code')
  if (!pre) return null
  return (pre.textContent ?? '').replace(/\n$/, '')
}

function showCopied(button: HTMLElement): void {
  const pending = resets.get(button)
  if (pending !== undefined) clearTimeout(pending)
  button.textContent = COPIED_LABEL
  button.setAttribute('aria-label', COPIED_LABEL)
  button.dataset.mdCopied = ''
  resets.set(
    button,
    setTimeout(() => {
      resets.delete(button)
      button.textContent = COPY_LABEL
      button.setAttribute('aria-label', COPY_ARIA)
      delete button.dataset.mdCopied
    }, COPIED_MS),
  )
}

/** Copies the code of the block `button` belongs to; true once it is on the clipboard. */
export async function copyCodeBlock(button: HTMLElement): Promise<boolean> {
  const text = codeBlockText(button)
  if (text === null) return false
  const copied = await copyText(text)
  if (copied) showCopied(button)
  return copied
}

function button(className: string, label: string, text?: string): HTMLButtonElement {
  const made = document.createElement('button')
  made.type = 'button'
  made.className = className
  made.setAttribute('aria-label', label)
  if (text !== undefined) made.textContent = text
  return made
}

/**
 * Puts the buttons in the blocks of `root`, as many times as it is called:
 * Copy on every code block, Run before it on a shell block when `run` is set
 * (and removed when it is not), and the zoom on every diagram.
 */
export function addBlockButtons(root: HTMLElement, run: boolean): void {
  for (const block of root.querySelectorAll<HTMLElement>('.md-code-block')) {
    const head = block.querySelector<HTMLElement>(':scope > .md-code-head')
    if (!head) continue
    if (!head.querySelector(':scope > button.md-code-copy')) head.append(button('md-code-copy', COPY_ARIA, COPY_LABEL))
    const existing = head.querySelector(':scope > button.md-code-run')
    if (run && block.classList.contains('md-code-shell')) {
      if (!existing) {
        const made = button('md-code-run', 'Run in the terminal', 'Run')
        made.title = 'Run in the terminal'
        head.querySelector(':scope > button.md-code-copy')!.before(made)
      }
    } else existing?.remove()
  }
  for (const block of root.querySelectorAll<HTMLElement>('.md-mermaid')) {
    if (block.querySelector(':scope > button.md-zoom')) continue
    const zoom = button('md-zoom', 'Expand diagram')
    zoom.title = 'Expand diagram'
    zoom.dataset.mdZoom = 'diagram'
    block.append(zoom)
  }
}
